import { motion } from "framer-motion";
import { AlertCircle, ArrowLeft, Lock, Mail } from "../../components/icons";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { UserAgreementsModal } from "../../components/UserAgreementsModal";
import { useAuth } from "../../contexts/AuthContext";
import { checkUserRole } from "../../lib/auth";
import { supabase } from "../../lib/supabase";

export function LoginPageTR() {
  const navigate = useNavigate();
  const { user, checkAgreements } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetSuccess, setResetSuccess] = useState(false);
  const [showAgreements, setShowAgreements] = useState(false);
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [credentials, setCredentials] = useState({ email: "", password: "" });

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password,
      });

      if (signInError) throw signInError;
      if (!data.user) throw new Error("Giris basarisiz");
    } catch (err: any) {
      if (err.message?.includes("Invalid login credentials")) {
        setError("Gecersiz e-posta veya sifre");
      } else if (err.message?.includes("Email not confirmed")) {
        setError("Lutfen e-posta adresinizi onaylayin");
      } else {
        setError(err.message || "Giris yapilamadi");
      }
      await supabase.auth.signOut();
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    setResetSuccess(false);

    try {
      const { error: fnError } = await supabase.functions.invoke("reset-password", {
        body: { email: resetEmail, locale: "tr" },
      });
      if (fnError) throw fnError;
      setResetSuccess(true);
    } catch (err: any) {
      setError(err.message || "Sifre sifirlama e-postasi gonderilemedi");
    } finally {
      setIsLoading(false);
    }
  };

  const redirectByRole = async (uid: string) => {
    const role = await checkUserRole(uid);
    switch (role) {
      case "admin":
        navigate("/admin");
        break;
      case "partner":
        navigate("/tr/partner");
        break;
      case "coach":
        navigate("/tr/coach");
        break;
      default:
        navigate("/tr/dashboard");
    }
  };

  const handleAgreementsSuccess = async () => {
    if (pendingUserId) {
      await redirectByRole(pendingUserId);
    }
  };

  const handleUserLoggedIn = async (uid: string) => {
    try {
      setIsLoading(true);
      setPendingUserId(uid);
      const hasAgreements = await checkAgreements(uid);
      if (!hasAgreements) {
        setShowAgreements(true);
        setIsLoading(false);
        return;
      }
      await redirectByRole(uid);
    } catch (err) {
      console.error("Error handling user logged in:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        supabase.auth.signOut().then(() => setIsReady(true));
      } else {
        setIsReady(true);
      }
    });
  }, []);

  useEffect(() => {
    if (isReady && user?.id) {
      handleUserLoggedIn(user.id);
    }
  }, [user, isReady]);

  return (
    <div className="min-h-screen freud-bg py-12 px-4">
      <div className="max-w-md mx-auto">
        <Link
          to="/tr"
          className="inline-flex items-center gap-2 text-chocolate-600 hover:text-sage-600 mb-8 transition-colors font-medium"
        >
          <ArrowLeft className="w-5 h-5" />
          Ana Sayfaya Don
        </Link>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
          <div className="flex justify-center mx-auto mb-6">
            <img src="/human_potential_os_logo_V2_cropped.png" alt="NLP OS" className="h-16 w-auto" />
          </div>
          <h1 className="freud-heading-2xl text-chocolate-900 mb-2">
            {showForgotPassword ? "Sifre Sifirla" : "Tekrar Hos Geldiniz"}
          </h1>
          <p className="freud-text-base text-chocolate-500">
            {showForgotPassword
              ? "Sifirlama talimatlari almak icin e-postanizi girin"
              : "Panoya erismek icin giris yapin"}
          </p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="freud-card-elevated p-8">
          {error && (
            <div className="flex items-center gap-2 bg-warm-100 text-warm-700 p-4 rounded-xl mb-6">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm">{error}</p>
            </div>
          )}

          {resetSuccess && (
            <div className="flex items-center gap-2 bg-sage-100 text-sage-700 p-4 rounded-xl mb-6">
              <Mail className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm">Yeni giris bilgileriniz e-posta ile gonderildi. Lutfen gelen kutunuzu kontrol edin.</p>
            </div>
          )}

          {showForgotPassword ? (
            <form onSubmit={handleForgotPassword} className="space-y-6">
              <div>
                <label htmlFor="resetEmail" className="freud-label block text-chocolate-800 mb-2">E-posta Adresi</label>
                <input type="email" id="resetEmail" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} className="freud-input" required disabled={isLoading} placeholder="E-posta adresinizi girin" />
              </div>
              <div className="flex justify-between items-center pt-4">
                <button type="button" onClick={() => setShowForgotPassword(false)} className="text-chocolate-600 hover:text-sage-600 transition-colors font-medium">Girise Don</button>
                <button type="submit" disabled={isLoading} className="freud-btn-primary inline-flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  {isLoading ? "Gonderiliyor..." : "Sifirlama Linki Gonder"}
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <label htmlFor="email" className="freud-label block text-chocolate-800 mb-2">E-posta Adresi</label>
                <input type="email" id="email" value={credentials.email} onChange={(e) => setCredentials((prev) => ({ ...prev, email: e.target.value }))} className="freud-input" required disabled={isLoading} placeholder="E-posta adresinizi girin" />
              </div>
              <div>
                <label htmlFor="password" className="freud-label block text-chocolate-800 mb-2">Sifre</label>
                <input type="password" id="password" value={credentials.password} onChange={(e) => setCredentials((prev) => ({ ...prev, password: e.target.value }))} className="freud-input" required disabled={isLoading} placeholder="Sifrenizi girin" />
              </div>
              <div className="flex justify-between items-center pt-4">
                <button type="button" onClick={() => setShowForgotPassword(true)} className="text-chocolate-600 hover:text-sage-600 transition-colors font-medium text-sm">Sifremi Unuttum?</button>
                <button type="submit" disabled={isLoading} className="freud-btn-primary inline-flex items-center gap-2">
                  <Lock className="w-5 h-5" />
                  {isLoading ? "Giris yapiliyor..." : "Giris Yap"}
                </button>
              </div>
            </form>
          )}
        </motion.div>
      </div>

      <UserAgreementsModal
        isOpen={showAgreements}
        onClose={() => setShowAgreements(false)}
        userId={pendingUserId}
        onSuccess={handleAgreementsSuccess}
      />
    </div>
  );
}
